/* eslint-disable max-len */
import verticals from 'i18n/messages/segments/verticals.en';
import guides from 'i18n/messages/segments/guides.en';

const en = {
  meta: {
    home: {
      title: 'GastroBuddy — One app for your floor + AI assistant (free month)',
      description:
        'Start your free month. Checklists, cash, recipes, time tracking, web dashboard — plus an AI assistant for owners. Setup takes minutes.',
    },
    onboard: {
      title: 'Sign up — GastroBuddy | Free trial',
      description:
        'Create your GastroBuddy account and start your free trial. Manage locations, enable modules, and invite your team.',
    },
    terms: {
      title: 'Terms of Service — GastroBuddy',
      description:
        'Terms of Service for using GastroBuddy software and hospitality operations tools.',
    },
    privacy: {
      title: 'Privacy Policy — GastroBuddy',
      description:
        'How GastroBuddy processes personal data for our website, dashboard, and mobile apps.',
    },
    cookies: {
      title: 'Cookies — GastroBuddy',
      description:
        'Information about cookies and similar technologies on gastrobuddy.nl and related sites.',
    },
    contact: {
      title: 'Contact — GastroBuddy',
      description: 'Contact GastroBuddy for support, sales, and privacy questions.',
    },
    compare: {
      title: 'GastroBuddy vs paper & generic tools',
      description:
        'See how GastroBuddy compares to paper binders, spreadsheets, and generic task apps for restaurant floor operations.',
    },
    checklist: {
      title: 'Free open/close checklist for restaurants — GastroBuddy',
      description:
        'A practical opening and closing checklist for hospitality teams. Use it as-is, then run it in GastroBuddy with your team.',
    },
    pricing: {
      title: 'Pricing — GastroBuddy plans & modules',
      description:
        'Choose a team-size plan and optional per-location modules. Checkout on the web, then unlock the app with your account.',
    },
    forIndex: {
      title: 'GastroBuddy for restaurants, hotels, bars & more',
      description:
        'See how GastroBuddy fits restaurants, hotels, bars, cafés, and catering — then start a free month.',
    },
    guidesIndex: {
      title: 'Hospitality ops guides — GastroBuddy',
      description:
        'Practical guides for open/close, staff onboarding, HACCP-style checks, cash closing, MEP, and multi-location ops.',
    },
    notFound: {
      title: 'Page not found — GastroBuddy',
      description: 'This page does not exist. Return to the GastroBuddy homepage.',
    },
  },
  nav: {
    home: 'Home',
    login: 'Log in',
    signup: 'Sign up free',
    language: 'Language',
    primary: 'Primary',
  },
  footer: {
    copyright: '© {year} GastroBuddy',
    terms: 'Terms of Service',
    privacy: 'Privacy',
    cookies: 'Cookies',
    contact: 'Contact',
    compare: 'Compare',
    checklist: 'Free checklist',
    pricing: 'Pricing',
    forVenues: 'For your venue',
    guides: 'Guides',
    language: 'Language',
    nav: 'Footer',
  },
  home: {
    heroTitle: 'One app for your floor: checklists, cash, recipes, and an AI assistant',
    heroSubtitle:
      'Cut busywork: create lists and recipes from chat, manage locations from the browser, and leave ring binders behind.',
    ctaPrimary: 'Start my free month',
    ctaPrimaryHint: 'First month free · Setup takes minutes',
    trustLabel: 'Why operators start here',
    trustFreeMonth: 'First month free',
    trustSetup: 'Setup in minutes',
    trustApps: 'iOS, Android & web',
    trustTeam: 'Invite staff when you are ready',
    stickyCtaLabel: 'Start free trial',
    stickyCta: 'Start free month',
    stickyCtaHint: 'No credit card to explore',
    compareLink: 'Compare options',
    checklistLink: 'Free open/close checklist',
    forLink: 'For restaurants, hotels & more',
    guidesLink: 'Ops guides',
    sectionReadyTitle: 'Ready to try it with your team?',
    sectionReadyBody:
      'Create an account in minutes and turn on the modules you need: task lists, cash, recipes, and more.',
    ctaSecondary: 'Create my free account',
    ctaSecondaryHint: 'Free trial · Invite staff when you are ready',
    downloadTitle: 'Also on iOS and Android',
    heroHomeAlt: 'GastroBuddy mobile home screen with daily operations overview',
    heroTasklistAlt: 'GastroBuddy task list screen for floor checklists',
    videoTitle: 'GastroBuddy product overview video',
    videoHeading: 'See it on the floor',
    videoLede: 'A short look at how GastroBuddy runs open, close, and daily ops with your team.',
    outcomesTitle: 'What changes on shift',
    outcomesLede: 'Less chasing, clearer ownership, faster onboarding for new staff.',
    outcomes: {
      oneTitle: 'One place for the floor',
      oneBody: 'Task progress in real time so everyone sees what is done and what is next.',
      twoTitle: 'Clear ownership',
      twoBody: 'Quality and accountability stay visible across shifts, not buried in chat.',
      threeTitle: 'Faster onboarding',
      threeBody: 'Step-by-step plans from open to close, so new staff start useful sooner.',
    },
    modulesTitle: 'Modules you turn on when you need them',
    modulesLede: 'Start with checklists. Add cash, recipes, and the rest as your venue needs them.',
    modules: {
      aiTitle: 'AI assistant',
      aiBody: 'Create lists and recipes from chat, pull locations, and draft reports.',
      tasklistTitle: 'Task lists',
      tasklistBody: 'Open/close and daily checklists with shared progress.',
      cashTitle: 'Cash closing',
      cashBody: 'Register tracking and day closings without spreadsheet chaos.',
      recipesTitle: 'Recipes',
      recipesBody: 'Ingredients and specs for kitchen and bar.',
      dashboardTitle: 'Web dashboard',
      dashboardBody: 'Manage locations, lists, and recipes from the browser.',
      mepTitle: 'Mise en place',
      mepBody: 'Prep lists that match how your stations actually run.',
      facilityTitle: 'Facility',
      facilityBody: 'Cleaning, maintenance, and floor ops lists your team can run daily.',
      whiteboardTitle: 'Team whiteboard',
      whiteboardBody: 'Shared notes for the floor, visible to the whole team.',
    },
  },
  onboard: {
    thankYouTitle: 'Thank you for signing up',
    thankYouBodyBefore: 'Log in on the dashboard to finish setting up your location.',
    thankYouLink: 'Open GastroBuddy dashboard',
  },
  forms: {
    signupTitle: 'Sign up',
    signupBullet1: 'Create an account in three quick steps. No strings attached.',
    signupBullet2: 'Add task lists and team members in the GastroBuddy Manager',
    signupBullet3: 'Your team can start using GastroBuddy right away',
    fullName: 'Full name',
    email: 'Email',
    password: 'Password',
    passwordTitle: 'Choose a password',
    passwordLede: 'Use at least 8 characters. You can show it while typing.',
    businessTitle: 'Business details',
    businessLede: 'Tell us about your venue so we can set up your location.',
    businessName: 'Business name',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal code',
    phone: 'Phone number',
    next: 'Next',
    back: 'Back',
    createAccount: 'Create account',
    creatingAccount: 'Creating account…',
    stepProgress: 'Step {current} of {total}',
    checkoutClaimNote:
      'Your checkout plan will be linked to this account after signup. No extra payment in the app.',
    passwordHint: 'At least 8 characters',
    showPassword: 'Show',
    hidePassword: 'Hide',
    googleContinue: 'Continue with Google',
    googleContinueLoading: 'Opening Google…',
    googleFailed: 'Google sign-in did not finish. Try again or use email.',
    googleSessionExpired: 'Your Google session expired. Continue with Google again.',
    orEmail: 'Or continue with email',
    postalHint: 'Dutch format, e.g. 1234 AB',
    phoneHint: 'Include country or area code if needed',
    valNameRequired: 'Enter your full name',
    valEmailRequired: 'Enter your email address',
    valEmailInvalid: 'Enter a valid email address',
    valPasswordMin: 'Password must be at least 8 characters',
    valPasswordRequired: 'Choose a password',
    valBusinessRequired: 'Enter your business name',
    valAddressRequired: 'Enter the street address',
    valCityRequired: 'Enter the city',
    valZipFormat: 'Use format 1234 AB',
    valZipRequired: 'Enter the postal code',
    valPhoneRequired: 'Enter a phone number',
    valPhoneMin: 'Phone number must be at least 8 characters',
    requestFailed: 'We could not create your account. Check your details and try again.',
    networkError: 'Connection failed. Check your network and try again.',
  },
  notFound: {
    title: 'Page not found',
    backHome: 'Back to home',
  },
  terms: {
    title: 'Terms of Service',
    intro:
      'These Terms of Service ("Terms") govern your use of GastroBuddy software and related services offered by us ("GastroBuddy", "we", "us"). By creating an account or using the service, you agree to these Terms.',
    notice:
      'This is a general website notice. Have your legal counsel review and replace this text for your entity and jurisdiction.',
    s1: '1. The service',
    s1Body:
      'GastroBuddy provides tools for hospitality operations (including but not limited to checklists, cash tracking, recipes, time registration, and related features). Features may vary by plan, location, and configuration.',
    s2: '2. Accounts and eligibility',
    s2Body:
      'You must provide accurate registration information and keep credentials secure. You are responsible for activity under your account. You must have authority to bind your business if you register on behalf of an organisation.',
    s3: '3. Acceptable use',
    s3Body:
      'You will not misuse the service, attempt unauthorised access, interfere with other users, or use GastroBuddy in violation of applicable law. We may suspend or terminate access for breach of these Terms or risk to the platform.',
    s4: '4. Subscriptions and fees',
    s4Body:
      'Paid plans, trials, and billing terms are presented at purchase or in your account. Unless stated otherwise, fees are non-refundable except where required by law. We may change pricing with reasonable notice.',
    s5: '5. Data and privacy',
    s5Before: 'Our processing of personal data is described in our',
    s5After:
      '. You are responsible for your own compliance with laws applicable to your business and staff data.',
    s6: '6. Disclaimer and limitation of liability',
    s6Body:
      'The service is provided "as is" to the extent permitted by law. We do not guarantee uninterrupted or error-free operation. Our total liability for claims arising from these Terms or the service is limited as permitted by applicable law.',
    s7: '7. Changes',
    s7Body:
      'We may update these Terms from time to time. Continued use after changes constitutes acceptance of the revised Terms where permitted by law.',
    s8: '8. Contact',
    s8Before: 'Questions about these Terms: see our',
    s8After: 'page.',
  },
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy explains how GastroBuddy ("we", "us") processes personal data when you use our website, web dashboard, and mobile applications.',
    notice:
      'Replace this text with a version reviewed by your Data Protection Officer or legal counsel, especially if you operate in the EU/UK (GDPR) or other regulated regions.',
    h1: '1. Who is responsible?',
    p1Before:
      'The controller of personal data for the service is the business entity operating GastroBuddy for your contract. For questions, use the details on our',
    p1After: 'page.',
    h2: '2. What data we process',
    p2Before:
      'Depending on how you use GastroBuddy, this may include: account and contact details (e.g. name, email), organisation and location information, usage and log data, content you enter (e.g. task lists, recipes, operational records), and technical data (e.g. device, IP address, cookies — see our',
    p2After: ').',
    cookieLink: 'Cookie notice',
    h3: '3. Purposes and legal bases',
    p3: 'We process data to provide and improve the service, authenticate users, bill subscriptions, communicate with you, ensure security, and comply with legal obligations. Where GDPR applies, we rely on contract, legitimate interests, consent (where required), and legal obligation as appropriate.',
    h4: '4. Processors and transfers',
    p4: 'We use infrastructure and subprocessors (e.g. hosting, authentication, analytics) who process data on our instructions. Data may be processed in the European Economic Area and other countries with appropriate safeguards where required.',
    h5: '5. Retention',
    p5: 'We keep data only as long as needed for the purposes above, including legal, accounting, and dispute resolution requirements, unless a longer period is required by law.',
    h6: '6. Your rights',
    p6: 'Where applicable, you may have rights to access, rectify, erase, restrict, or object to processing, data portability, and to lodge a complaint with a supervisory authority. Contact us to exercise your rights.',
    h7: '7. Changes',
    p7: 'We may update this policy from time to time. We will post the updated version on this page and, if appropriate, notify you through the service.',
  },
  cookies: {
    title: 'Cookies & similar technologies',
    intro:
      'This page describes how we use cookies and similar technologies on gastrobuddy.nl and related web properties.',
    h1: '1. What are cookies?',
    p1: 'Cookies are small files stored on your device. We also use similar technologies (e.g. local storage) where needed for the service to function.',
    h2: '2. How we use them',
    essential: 'Essential: required for security, load balancing, and core site functionality.',
    preferences: 'Preferences: remember choices such as language where applicable.',
    analytics:
      'Analytics / marketing: we may use tools (e.g. Google Tag Manager) to understand traffic and campaigns, subject to your consent where required.',
    h3: '3. Managing cookies',
    p3: 'You can control cookies through your browser settings. Blocking some cookies may limit site functionality.',
    h4: '4. More information',
    p4Before: 'Personal data related to cookies is covered in our',
    p4After: '.',
  },
  contact: {
    title: 'Contact',
    intro: 'We are happy to help with questions about GastroBuddy, your account, or partnerships.',
    customers: 'Existing customers',
    customersBody:
      'Log in to the web dashboard for account and location management: use Log in from the header of this site, or go directly to your manager URL provided during onboarding.',
    legal: 'Legal & privacy',
    legalBefore:
      'For privacy requests or legal notices, please contact us using the same channels above and include "Privacy" in the subject line. See also our',
    legalMid: 'and',
    legalAfter: '.',
  },
  pricing: {
    title: 'GastroBuddy pricing',
    intro:
      'Pick a base plan by team size, add modules per location, then continue to secure checkout. Or start a free trial first.',
    cancelled: 'Checkout was cancelled. Adjust your plan below when you are ready.',
    tiersHeading: 'Organization plan',
    tiersHint: 'Staff limits apply per location.',
    staffStarter: 'Up to 5 team members',
    staffPro: '6–20 team members',
    staffEnterprise: 'Unlimited team members',
    tiers: {
      starter: { name: 'Starter' },
      pro: { name: 'Pro' },
      enterprise: { name: 'Enterprise' },
    },
    modulesHeading: 'Location modules',
    modulesHint: 'Priced per location / month. Task lists and recipes are included in every plan.',
    included: 'Included: task lists + recipes',
    modules: {
      modCash: 'Cash register',
      modTimesheet: 'Timesheet',
      modReservations: 'Reservations',
      modMep: 'Mise en place',
      modFacility: 'Facility',
    },
    summaryHeading: 'Your monthly total',
    summaryNote: 'Add-ons assume one location. Extra locations are managed after signup.',
    total: 'Total',
    perMonth: '/mo',
    perLocationMonth: '/loc/mo',
    checkoutCta: 'Continue to checkout',
    checkoutLoading: 'Opening checkout…',
    trialCta: 'Start free trial instead',
    enterpriseContact: 'Prefer to talk? Contact us',
    footnote:
      'Subscriptions are billed on the web via Stripe. The app unlocks after you sign in with an active account. Prices exclude VAT where applicable.',
    errors: {
      apiMissing: 'Billing is not configured on this site. Try again later or contact us.',
      checkoutFailed: 'Could not start checkout. Check your connection and try again.',
      network: 'Network error. Check your connection and try again.',
    },
  },
  compare: {
    title: 'How GastroBuddy compares',
    intro:
      'Operators usually start with paper, spreadsheets, WhatsApp, or a POS that was never built for floor checklists. Here is an honest category comparison — not a vendor hit list.',
    tableCaption: 'Comparison of paper, generic tools, and GastroBuddy for hospitality operations',
    colTopic: 'Need',
    colPaper: 'Paper / binders',
    colGeneric: 'Spreadsheets & chat',
    colGastro: 'GastroBuddy',
    rows: {
      progress: {
        topic: 'Live task progress',
        paper: 'No shared status; managers chase updates',
        generic: 'Messages get buried; hard to see what is done',
        gastro: 'Real-time progress per task for the whole team',
      },
      cash: {
        topic: 'Cash & day closings',
        paper: 'Separate notebook or till sheet',
        generic: 'Ad-hoc sheets; easy to lose history',
        gastro: 'Cash register tracking and day closings in one place',
      },
      recipes: {
        topic: 'Recipes & mise en place',
        paper: 'Printed folders go out of date',
        generic: 'Docs scattered across drives',
        gastro: 'Recipes and MEP lists with kitchen and bar context',
      },
      ai: {
        topic: 'Creating lists & recipes',
        paper: 'Rewrite by hand',
        generic: 'Copy-paste templates',
        gastro: 'AI assistant can draft lists and recipes from chat',
      },
      team: {
        topic: 'Onboarding new staff',
        paper: 'Shadow shifts and tribal knowledge',
        generic: 'Long message threads',
        gastro: 'Step-by-step plans your team can follow on phone or tablet',
      },
      paper: {
        topic: 'Leaving the binder behind',
        paper: 'Still carrying it',
        generic: 'Still printing “just in case”',
        gastro: 'Same account on mobile apps and web dashboard',
      },
    },
    disclaimer:
      'We do not claim every venue needs every module. Start with the free month, turn on what you use, and invite staff when you are ready.',
    cta: 'Start my free month',
    ctaHint: 'First month free · Setup takes minutes',
    checklistLink: 'Prefer a printable starter? Get the free open/close checklist →',
  },
  checklist: {
    title: 'Free open/close checklist',
    intro:
      'A practical starting point for hospitality floor routines. Adapt it to your venue, then run the same flow in GastroBuddy so the whole team sees progress.',
    openTitle: 'Opening',
    closeTitle: 'Closing',
    open: {
      o1: 'Unlock, lights, music, and climate as needed',
      o2: 'Check toilets, floors, and guest-facing areas',
      o3: 'Stock stations, ice, garnishes, and to-go supplies',
      o4: 'Confirm tills / floats and card terminals are ready',
      o5: 'Brief the team on reservations, 86s, and allergens',
      o6: 'Verify fridge temps and critical food-safety checks',
    },
    close: {
      c1: 'Clear, reset, and restock stations for the next shift',
      c2: 'Run final cash / tip reconciliation and day closing',
      c3: 'Clean guest areas, toilets, and high-touch surfaces',
      c4: 'Secure inventory, fridges, and alcohol storage',
      c5: 'Complete closing checklist items assigned to the shift',
      c6: 'Lock up, alarms, and note anything for the next open',
    },
    adaptNote:
      'Treat this as a template. Your venue may need HACCP-specific steps, bar-only items, or multi-location variants — GastroBuddy is built for that structure.',
    formTitle: 'Email me this checklist',
    formIntro: 'Optional: leave your work email and we send the checklist to your inbox. No spam.',
    formEmail: 'Work email',
    formBusiness: 'Business name (optional)',
    formSubmit: 'Send me the checklist',
    formSubmitting: 'Sending…',
    formError: 'Something went wrong. Try again, or start your free account instead.',
    formEmailRequired: 'Enter a work email so we can send the checklist.',
    formEmailInvalid: 'Enter a valid email address (for example name@restaurant.nl).',
    formSuccessTitle: 'Thanks — check your inbox',
    formSuccessBody: 'The checklist is on its way. You can also start your free month now.',
    formPrivacyBefore:
      'We only use your details to email this resource and relevant GastroBuddy tips. See',
    formPrivacyAfter: ' for details.',
    nextTitle: 'Ready to run this with your team?',
    nextBody: 'Turn the same open/close flow into live task lists with progress everyone can see.',
    cta: 'Start my free month',
    ctaHint: 'Free trial · Invite staff when you are ready',
    compareLink: 'See how GastroBuddy compares to paper and spreadsheets →',
  },
  jsonLd: {
    orgDescription:
      'GastroBuddy helps restaurants and bars run daily operations: checklists, cash register, recipes, time tracking, and an AI assistant for owners.',
    siteDescription:
      'Hospitality operations platform: task lists, cash, mise en place, recipes with kitchen and bar contexts, web dashboard, and AI assistant.',
  },
  verticals,
  guides,
} as const;

export default en;



// WEBPACK FOOTER //
// ./src/i18n/messages/en.ts